/**
 * AccountSummary - 계좌 요약 컴포넌트
 *
 * CRITICAL: 절대 목업 데이터 사용 금지!
 * - 데이터 없으면: "데이터 없음" 명확히 표시
 * - API 오류 시: "API 오류: {상세 메시지}" 표시
 * - 로딩 중: "로딩 중..." 스피너 표시
 * - 실제 운영 API 데이터만 사용
 */
import React, { useEffect, useState } from 'react';
import { fetchPortfolio } from '../services/portfolioApi';
import { AccountInfo } from '../types/portfolio';
import { formatCurrency } from '../utils/formatNumber';
import StatCard from './StatCard';
import './AccountSummary.css';

const AccountSummary: React.FC = () => {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAccount();
  }, []);

  const loadAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPortfolio();
      setAccount(data.account);
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  };

  // 실제 데이터만 표시
  if (loading) {
    return (
      <div className="account-summary">
        <h3>계좌 정보 <button onClick={loadAccount} className="refresh-btn">새로고침</button></h3>
        <div className="loading">
          <div className="spinner"></div>
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-summary">
        <h3>계좌 정보 <button onClick={loadAccount} className="refresh-btn">새로고침</button></h3>
        <div className="error">
          <p>API 오류: {error}</p>
          <button onClick={loadAccount}>다시 시도</button>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="account-summary">
        <h3>계좌 정보 <button onClick={loadAccount} className="refresh-btn">새로고침</button></h3>
        <div className="empty">
          <p>계좌 정보가 없습니다</p>
          <button onClick={loadAccount}>새로고침</button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-summary">
      <h3>계좌 정보 <button onClick={loadAccount} className="refresh-btn">새로고침</button></h3>
      <div className="stats-grid">
        <StatCard
          label="현금"
          value={formatCurrency(account.cash)}
        />
        <StatCard
          label="총 자산"
          value={formatCurrency(account.portfolio_value)}
        />
        <StatCard
          label="매수력"
          value={formatCurrency(account.buying_power)}
        />
        <StatCard
          label="일일 손익"
          value={`${formatCurrency(account.daily_return_amount)} (${account.daily_return_percent >= 0 ? '+' : ''}${account.daily_return_percent.toFixed(2)}%)`}
          className={account.daily_return_amount >= 0 ? 'positive' : 'negative'}
        />
        {account.initial_deposit && account.total_return_percent !== undefined && (
          <StatCard
            label="총 수익률"
            value={`${account.total_return_percent >= 0 ? '+' : ''}${account.total_return_percent.toFixed(2)}% (${formatCurrency(account.total_return_amount || 0)})`}
            className={account.total_return_percent >= 0 ? 'positive' : 'negative'}
          />
        )}
      </div>
    </div>
  );
};

export default AccountSummary;
